import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useWorkOrderRecord } from "@/app/ProDrivers/WorkOrders/Hooks/useWorkOrderRecord";
import {
  ClipboardDocumentListIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@/components/catalyst/button";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { CreateQuoteDialog } from "@/app/Quotes/Components/CreateQuoteDialog";
import { getQuoteInitialFieldsFromWorkOrder } from "@/app/ProDrivers/WorkOrders/utils/getQuoteInitialFieldsFromWorkOrder";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { formatDate } from "@/lib/Formatters/formatDate";
import { Badge } from "@/components/catalyst/badge";
import { QuoteStatus } from "@/gql/graphql";
import { useNavigate } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import { IconMenuActions } from "@/lib/Components/Actions/IconMenuActions";
import { useQuoteActions } from "@/app/Quotes/hooks/useQuoteActions";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { createQuotePdfMutation } from "@/app/Quotes/GraphQL/quoteMutations";
import { DeclineQuoteDialog } from "@/app/Quotes/Components/Dialogs/DeclineQuoteDialog";
import { AcceptQuoteDialog } from "@/app/Quotes/Components/Dialogs/AcceptQuoteDialog";
import { CheckIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { PreviewPdfDialog } from "@/lib/Components/Dialog/PreviewPdfDialog";
import { PDFIcon } from "@/assets/Icons";
import { QuoteDeclinedAlert } from "@/app/Quotes/Components/QuoteDeclinedAlert";

const quoteStatusColorMap: Record<
  QuoteStatus,
  "zinc" | "yellow" | "green" | "red"
> = {
  [QuoteStatus.Draft]: "zinc",
  [QuoteStatus.Issued]: "yellow",
  [QuoteStatus.Accepted]: "green",
  [QuoteStatus.Rejected]: "red",
  [QuoteStatus.Expired]: "zinc",
  [QuoteStatus.Voided]: "red",
  [QuoteStatus.Invoiced]: "green",
};

export function WorkOrderQuoteCard() {
  const workOrder = useWorkOrderRecord();
  const { open } = useDialog(CreateQuoteDialog);
  const { open: openPdf } = useDialog(PreviewPdfDialog);
  const navigate = useNavigate();
  const quote = workOrder.currentQuote;
  const tStatus = useTranslations("quote.status");
  const t = useTranslations("workOrder.cards.quote");
  const getActions = useQuoteActions();
  const { isAdmin } = useTenant();
  const { open: openAcceptDialog } = useDialog(AcceptQuoteDialog);
  const { open: openDeclineDialog } = useDialog(DeclineQuoteDialog);
  const { mutateAsync: createPdf, isPending: isCreatingPdf } = useGqlMutation(
    createQuotePdfMutation,
  );

  const showAcceptDeclineButtons = quote?.status === QuoteStatus.Issued;

  const openDrawer = () => {
    if (!quote) return;
    navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        quoteId: quote.id,
      }),
    });
  };

  const handlePreviewPdf = async () => {
    if (!quote) return;
    const url = await createPdf({ id: quote.id });
    openPdf({ url: url.createQuotePdf });
  };

  if (!quote) {
    return (
      <Card
        className={
          isAdmin
            ? "cursor-pointer border-2 border-dashed border-gray-300 transition-colors hover:border-gray-400 hover:bg-gray-50"
            : "border-2 border-dashed border-gray-300"
        }
        onClick={
          isAdmin
            ? () => {
                open({
                  issueImmediately: true,
                  initialValues: getQuoteInitialFieldsFromWorkOrder(workOrder),
                });
              }
            : undefined
        }
      >
        <CardContent className="flex flex-col items-center justify-center py-8">
          <div className="flex size-12 items-center justify-center rounded-full bg-gray-100">
            <ClipboardDocumentListIcon className="size-6 text-gray-400" />
          </div>
          <h3 className="mt-3 text-sm font-medium text-gray-900">
            {t("title")}
          </h3>
          <p className="mt-1 text-xs text-gray-500">{t("noQuoteYet")}</p>
          {isAdmin ? (
            <Button outline className="pointer-events-none mt-4">
              <PlusIcon className="h-4 w-4" />
              {t("createQuote")}
            </Button>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={
        isAdmin
          ? "cursor-pointer transition-shadow hover:shadow-md"
          : "transition-shadow"
      }
      onClick={isAdmin ? openDrawer : undefined}
    >
      <CardHeader className="border-b px-4 py-3">
        <CardTitle className="flex items-center justify-between text-base">
          <div className="flex items-center gap-2">
            <ClipboardDocumentListIcon className="h-4 w-4 text-gray-500" />
            <span>{t("title")}</span>
            <Badge color={quoteStatusColorMap[quote.status]}>
              {tStatus(quote.status)}
            </Badge>
          </div>
          <div
            className="flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            {showAcceptDeclineButtons ? (
              <>
                <Button
                  outline
                  onClick={() => openDeclineDialog({ quoteId: quote.id })}
                >
                  <XMarkIcon className="h-4 w-4" />
                  {t("decline")}
                </Button>
                <Button
                  color="green"
                  onClick={() =>
                    openAcceptDialog({
                      quoteId: quote.id,
                      purchaseOrderNumber: quote.purchase_order_number,
                    })
                  }
                >
                  <CheckIcon className="h-4 w-4" />
                  {t("accept")}
                </Button>
              </>
            ) : null}
            <Button outline onClick={handlePreviewPdf} disabled={isCreatingPdf}>
              <PDFIcon className="h-4 w-4" />
              {t("previewPdf")}
            </Button>
            {isAdmin ? <IconMenuActions actions={getActions(quote)} /> : null}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 p-4">
        <dl className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <dt className="text-gray-500">{t("reference")}</dt>
            <dd className="font-medium">{quote.reference}</dd>
          </div>
          {quote.purchase_order_number ? (
            <div>
              <dt className="text-gray-500">{t("purchaseOrderNumber")}</dt>
              <dd className="font-medium">{quote.purchase_order_number}</dd>
            </div>
          ) : null}
          <div>
            <dt className="text-gray-500">{t("quoteDate")}</dt>
            <dd className="font-medium">{formatDate(quote.quote_date)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">{t("expiryDate")}</dt>
            <dd className="font-medium">{formatDate(quote.expiry_date)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">{t("amountExcTax")}</dt>
            <dd className="font-medium">
              {formatCurrency(quote.amount_exc_tax, quote.currency)}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">{t("amountIncTax")}</dt>
            <dd className="font-medium">
              {formatCurrency(quote.amount_inc_tax, quote.currency)}
            </dd>
          </div>
        </dl>

        <QuoteDeclinedAlert quote={quote} />
      </CardContent>
    </Card>
  );
}
